import type { PriceModel } from "../src/types";
import { monthlyCost } from "./pricing";
import type { Language } from "./languages";
import { CALCULATOR_PATH } from "./routes";

export { CALCULATOR_PATH };

// Finite, validated bounds keep the calculator inputs identical on the server
// (SSR snapshot / canonicalization) and the client (React form + share URL).
export const REQUEST_COST_BOUNDS = {
  dailyRequests: { min: 0, max: 100_000_000 },
  inputTokensPerRequest: { min: 0, max: 2_000_000 },
  outputTokensPerRequest: { min: 0, max: 2_000_000 },
  billingDays: { min: 1, max: 366 },
  cacheHitPercent: { min: 0, max: 100 },
} as const;

export type RequestCostField = keyof typeof REQUEST_COST_BOUNDS;

export const REQUEST_COST_DEFAULTS: Record<RequestCostField, number> = {
  dailyRequests: 1_000,
  inputTokensPerRequest: 1_000,
  outputTokensPerRequest: 300,
  billingDays: 30,
  cacheHitPercent: 0,
};

export type RequestCostInput = Record<RequestCostField, number>;

const QUERY_KEYS: Record<RequestCostField, string> = {
  dailyRequests: "dailyRequests",
  inputTokensPerRequest: "inputTokens",
  outputTokensPerRequest: "outputTokens",
  billingDays: "billingDays",
  cacheHitPercent: "cacheHit",
};

// Clamps to a finite bounded number, falling back to the field default for
// anything missing, non-numeric, negative-below-min, or non-finite (NaN/Infinity).
export function clampRequestCostField(field: RequestCostField, raw: string | number | null | undefined): number {
  const bounds = REQUEST_COST_BOUNDS[field];
  const fallback = REQUEST_COST_DEFAULTS[field];
  if (raw === null || raw === undefined || raw === "") return fallback;
  const parsed = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(bounds.max, Math.max(bounds.min, parsed));
}

export function requestCostInputFromSearch(search: URLSearchParams): RequestCostInput {
  return {
    dailyRequests: clampRequestCostField("dailyRequests", search.get(QUERY_KEYS.dailyRequests)),
    inputTokensPerRequest: clampRequestCostField("inputTokensPerRequest", search.get(QUERY_KEYS.inputTokensPerRequest)),
    outputTokensPerRequest: clampRequestCostField("outputTokensPerRequest", search.get(QUERY_KEYS.outputTokensPerRequest)),
    billingDays: clampRequestCostField("billingDays", search.get(QUERY_KEYS.billingDays)),
    cacheHitPercent: clampRequestCostField("cacheHitPercent", search.get(QUERY_KEYS.cacheHitPercent)),
  };
}

export function requestCostSearchParams(input: RequestCostInput, modelUid?: string): Record<string, string | number | undefined> {
  return {
    [QUERY_KEYS.dailyRequests]: input.dailyRequests,
    [QUERY_KEYS.inputTokensPerRequest]: input.inputTokensPerRequest,
    [QUERY_KEYS.outputTokensPerRequest]: input.outputTokensPerRequest,
    [QUERY_KEYS.billingDays]: input.billingDays,
    [QUERY_KEYS.cacheHitPercent]: input.cacheHitPercent,
    model: modelUid,
  };
}

// A missing/empty uid (no ?model= in the URL yet) defaults to the first catalog model so the
// calculator has something to show. An explicit uid that isn't in the catalog is a genuinely
// unknown/unavailable model and must NOT silently fall back to a different model's pricing --
// callers should treat `undefined` here as "show an unavailable state", not "pick one for me".
export function resolveCalculatorModel(models: PriceModel[], uid: string | null): PriceModel | undefined {
  if (!uid) return models[0];
  return models.find((model) => model.uid === uid);
}

export type RequestCostEstimate = {
  totalRequests: number;
  inputMillions: number;
  outputMillions: number;
  monthly: number;
  per1000Requests: number;
};

// Reuses the same per-token cost formula as the rest of the site (src/utils#monthlyCost);
// this function only converts request-volume inputs into the millions-of-tokens shape it expects.
export function estimateRequestCost(model: PriceModel, input: RequestCostInput): RequestCostEstimate {
  const totalRequests = input.dailyRequests * input.billingDays;
  const inputMillions = (totalRequests * input.inputTokensPerRequest) / 1_000_000;
  const outputMillions = (totalRequests * input.outputTokensPerRequest) / 1_000_000;
  const monthly = monthlyCost(model, inputMillions, outputMillions, input.cacheHitPercent);
  const per1000Requests = totalRequests > 0 ? (monthly / totalRequests) * 1_000 : 0;
  return { totalRequests, inputMillions, outputMillions, monthly, per1000Requests };
}

export type RequestCostCopy = {
  kicker: string;
  title: string;
  intro: string;
  dailyRequestsLabel: string;
  inputTokensLabel: string;
  outputTokensLabel: string;
  billingDaysLabel: string;
  cacheHitLabel: string;
  modelLabel: string;
  monthlyEstimateLabel: string;
  per1000Label: string;
  totalRequestsLabel: string;
  scopeTitle: string;
  scopeBody: string;
  noCachePricingNotice: string;
  modelLinkLabel: string;
};

const copy: Record<Language, RequestCostCopy> = {
  en: {
    kicker: "Request-volume calculator",
    title: "LLM API cost calculator",
    intro: "Estimate monthly API spend from daily request volume, tokens per request, and prompt-cache hit rate.",
    dailyRequestsLabel: "Daily requests",
    inputTokensLabel: "Input tokens / request",
    outputTokensLabel: "Output tokens / request",
    billingDaysLabel: "Billing days",
    cacheHitLabel: "Cache-read hit rate",
    modelLabel: "Model",
    monthlyEstimateLabel: "Estimated cost over the billing period",
    per1000Label: "Estimated cost / 1,000 requests",
    totalRequestsLabel: "Total requests in period",
    scopeTitle: "What this estimate covers",
    scopeBody: "A first-party, USD, text-generation API estimate using each provider's standard input, cached-input, and output token rates. It excludes prompt-cache writes, tool/function-calling tokens, retries, hosting, and volume or enterprise pricing tiers not reflected in the public rate card. It is not a full projected bill.",
    noCachePricingNotice: "This model has no published cache-read rate, so cached requests are estimated at the standard input rate — no savings are assumed.",
    modelLinkLabel: "View full pricing for this model",
  },
  ja: {
    kicker: "リクエスト量計算機",
    title: "LLM API コスト計算機",
    intro: "1日あたりのリクエスト数、リクエストごとのトークン数、キャッシュ命中率から月額APIコストを見積もります。",
    dailyRequestsLabel: "1日あたりのリクエスト数",
    inputTokensLabel: "リクエストあたりの入力トークン",
    outputTokensLabel: "リクエストあたりの出力トークン",
    billingDaysLabel: "課金対象日数",
    cacheHitLabel: "キャッシュ命中率",
    modelLabel: "モデル",
    monthlyEstimateLabel: "課金期間の見積もりコスト",
    per1000Label: "1,000リクエストあたりの見積もりコスト",
    totalRequestsLabel: "期間内の総リクエスト数",
    scopeTitle: "この見積もりの範囲",
    scopeBody: "各社の標準的な入力・キャッシュ入力・出力トークン単価を用いた、米ドル建ての一次提供元テキスト生成APIの見積もりです。キャッシュ書き込み、ツール／関数呼び出しのトークン、リトライ、ホスティング、公開料金表に反映されない大口・エンタープライズ料金は含みません。実際の請求額そのものではありません。",
    noCachePricingNotice: "このモデルにはキャッシュ読み取り料金の公開情報がないため、キャッシュ対象リクエストは標準入力料金で見積もっています。割引は仮定していません。",
    modelLinkLabel: "このモデルの詳細な料金を見る",
  },
  fr: {
    kicker: "Calculateur par volume de requêtes",
    title: "Calculateur de coût API LLM",
    intro: "Estimez la dépense API mensuelle à partir du volume de requêtes quotidien, des tokens par requête et du taux de succès du cache.",
    dailyRequestsLabel: "Requêtes quotidiennes",
    inputTokensLabel: "Tokens d’entrée / requête",
    outputTokensLabel: "Tokens de sortie / requête",
    billingDaysLabel: "Jours de facturation",
    cacheHitLabel: "Taux de succès du cache",
    modelLabel: "Modèle",
    monthlyEstimateLabel: "Coût estimé sur la période de facturation",
    per1000Label: "Coût estimé / 1 000 requêtes",
    totalRequestsLabel: "Requêtes totales sur la période",
    scopeTitle: "Ce que couvre cette estimation",
    scopeBody: "Une estimation en USD de l’API texte du fournisseur d’origine, à partir des tarifs standards d’entrée, d’entrée en cache et de sortie. Elle exclut les écritures de cache, les tokens d’appel d’outils/fonctions, les nouvelles tentatives, l’hébergement et les paliers tarifaires volume/entreprise non publiés. Ce n’est pas une facture complète projetée.",
    noCachePricingNotice: "Ce modèle ne publie pas de tarif de lecture du cache : les requêtes mises en cache sont donc estimées au tarif d’entrée standard, sans économie supposée.",
    modelLinkLabel: "Voir le tarif complet de ce modèle",
  },
  de: {
    kicker: "Anfragevolumen-Rechner",
    title: "LLM-API-Kostenrechner",
    intro: "Schätzen Sie die monatlichen API-Kosten anhand des täglichen Anfragevolumens, der Tokens pro Anfrage und der Cache-Trefferquote.",
    dailyRequestsLabel: "Tägliche Anfragen",
    inputTokensLabel: "Eingabe-Tokens / Anfrage",
    outputTokensLabel: "Ausgabe-Tokens / Anfrage",
    billingDaysLabel: "Abrechnungstage",
    cacheHitLabel: "Cache-Trefferquote",
    modelLabel: "Modell",
    monthlyEstimateLabel: "Geschätzte Kosten im Abrechnungszeitraum",
    per1000Label: "Geschätzte Kosten / 1.000 Anfragen",
    totalRequestsLabel: "Anfragen gesamt im Zeitraum",
    scopeTitle: "Was diese Schätzung abdeckt",
    scopeBody: "Eine USD-Schätzung der originären Text-API anhand der Standardpreise für Eingabe, zwischengespeicherte Eingabe und Ausgabe. Ausgeschlossen sind Cache-Schreibvorgänge, Tool-/Funktionsaufruf-Tokens, Wiederholungsversuche, Hosting sowie Volumen- oder Enterprise-Tarife, die nicht in der öffentlichen Preisliste stehen. Dies ist keine vollständige, tatsächliche Rechnung.",
    noCachePricingNotice: "Für dieses Modell ist kein Cache-Lesepreis veröffentlicht; zwischengespeicherte Anfragen werden daher zum Standard-Eingabepreis geschätzt — es wird keine Ersparnis angenommen.",
    modelLinkLabel: "Vollständige Preise für dieses Modell ansehen",
  },
  pt: {
    kicker: "Calculadora por volume de requisições",
    title: "Calculadora de custo de API de LLM",
    intro: "Estime o gasto mensal com API a partir do volume diário de requisições, tokens por requisição e taxa de acerto de cache.",
    dailyRequestsLabel: "Requisições diárias",
    inputTokensLabel: "Tokens de entrada / requisição",
    outputTokensLabel: "Tokens de saída / requisição",
    billingDaysLabel: "Dias de faturamento",
    cacheHitLabel: "Taxa de acerto de cache",
    modelLabel: "Modelo",
    monthlyEstimateLabel: "Custo estimado no período de faturamento",
    per1000Label: "Custo estimado / 1.000 requisições",
    totalRequestsLabel: "Total de requisições no período",
    scopeTitle: "O que esta estimativa cobre",
    scopeBody: "Uma estimativa em USD da API de texto do provedor de origem, usando as tarifas padrão de entrada, entrada em cache e saída. Exclui gravações de cache, tokens de chamadas de ferramentas/funções, novas tentativas, hospedagem e níveis de preço por volume ou empresariais não refletidos na tabela pública. Não é uma fatura completa projetada.",
    noCachePricingNotice: "Este modelo não publica tarifa de leitura de cache, portanto as requisições em cache são estimadas pela tarifa de entrada padrão — nenhuma economia é presumida.",
    modelLinkLabel: "Ver o preço completo deste modelo",
  },
  es: {
    kicker: "Calculadora por volumen de solicitudes",
    title: "Calculadora de costo de API de LLM",
    intro: "Estima el gasto mensual de API a partir del volumen diario de solicitudes, los tokens por solicitud y la tasa de aciertos de caché.",
    dailyRequestsLabel: "Solicitudes diarias",
    inputTokensLabel: "Tokens de entrada / solicitud",
    outputTokensLabel: "Tokens de salida / solicitud",
    billingDaysLabel: "Días de facturación",
    cacheHitLabel: "Tasa de aciertos de caché",
    modelLabel: "Modelo",
    monthlyEstimateLabel: "Costo estimado en el período de facturación",
    per1000Label: "Costo estimado / 1.000 solicitudes",
    totalRequestsLabel: "Solicitudes totales en el período",
    scopeTitle: "Qué cubre esta estimación",
    scopeBody: "Una estimación en USD de la API de texto de origen, usando las tarifas estándar de entrada, entrada en caché y salida de cada proveedor. Excluye escrituras de caché, tokens de llamadas a herramientas/funciones, reintentos, hospedaje y niveles de precio por volumen o empresariales no reflejados en la tarifa pública. No es una factura completa proyectada.",
    noCachePricingNotice: "Este modelo no publica una tarifa de lectura de caché, por lo que las solicitudes en caché se estiman a la tarifa de entrada estándar; no se asume ningún ahorro.",
    modelLinkLabel: "Ver el precio completo de este modelo",
  },
  ar: {
    kicker: "حاسبة حجم الطلبات",
    title: "حاسبة تكلفة واجهة برمجة تطبيقات LLM",
    intro: "قدّر الإنفاق الشهري على واجهة البرمجة استنادًا إلى عدد الطلبات اليومية وعدد الرموز لكل طلب ومعدل إصابة التخزين المؤقت.",
    dailyRequestsLabel: "الطلبات اليومية",
    inputTokensLabel: "رموز الإدخال لكل طلب",
    outputTokensLabel: "رموز الإخراج لكل طلب",
    billingDaysLabel: "أيام الفوترة",
    cacheHitLabel: "معدل إصابة التخزين المؤقت",
    modelLabel: "النموذج",
    monthlyEstimateLabel: "التكلفة المقدّرة خلال فترة الفوترة",
    per1000Label: "التكلفة المقدّرة لكل 1000 طلب",
    totalRequestsLabel: "إجمالي الطلبات خلال الفترة",
    scopeTitle: "ما الذي يغطيه هذا التقدير",
    scopeBody: "تقدير بالدولار الأمريكي لواجهة النصوص الرسمية من المزوّد، باستخدام أسعار الإدخال والإدخال المخزَّن مؤقتًا والإخراج القياسية. يستثني عمليات كتابة التخزين المؤقت، ورموز استدعاء الأدوات/الدوال، وإعادة المحاولات، والاستضافة، وشرائح أسعار الحجم أو المؤسسات غير الظاهرة في جدول الأسعار العام. وهو ليس فاتورة فعلية كاملة.",
    noCachePricingNotice: "لا يوجد لهذا النموذج سعر منشور لقراءة التخزين المؤقت، لذا تُقدَّر الطلبات المخزَّنة مؤقتًا بسعر الإدخال القياسي — دون افتراض أي توفير.",
    modelLinkLabel: "عرض السعر الكامل لهذا النموذج",
  },
  it: {
    kicker: "Calcolatore per volume di richieste",
    title: "Calcolatore costi API LLM",
    intro: "Stima la spesa mensile per l’API in base al volume giornaliero di richieste, ai token per richiesta e al tasso di hit della cache.",
    dailyRequestsLabel: "Richieste giornaliere",
    inputTokensLabel: "Token di input / richiesta",
    outputTokensLabel: "Token di output / richiesta",
    billingDaysLabel: "Giorni di fatturazione",
    cacheHitLabel: "Tasso di hit della cache",
    modelLabel: "Modello",
    monthlyEstimateLabel: "Costo stimato nel periodo di fatturazione",
    per1000Label: "Costo stimato / 1.000 richieste",
    totalRequestsLabel: "Richieste totali nel periodo",
    scopeTitle: "Cosa copre questa stima",
    scopeBody: "Una stima in USD dell’API testuale del fornitore originale, basata sulle tariffe standard di input, input in cache e output. Esclude le scritture in cache, i token per chiamate a strumenti/funzioni, i nuovi tentativi, l’hosting e i livelli tariffari per volume o enterprise non riportati nel listino pubblico. Non è una fattura reale completa.",
    noCachePricingNotice: "Questo modello non pubblica una tariffa di lettura cache, quindi le richieste in cache sono stimate alla tariffa di input standard, senza ipotizzare risparmi.",
    modelLinkLabel: "Vedi il prezzo completo di questo modello",
  },
  "zh-CN": {
    kicker: "请求量计算器",
    title: "LLM API 成本计算器",
    intro: "根据每日请求量、每次请求的 Token 数以及缓存命中率估算每月 API 支出。",
    dailyRequestsLabel: "每日请求数",
    inputTokensLabel: "每次请求输入 Token",
    outputTokensLabel: "每次请求输出 Token",
    billingDaysLabel: "计费天数",
    cacheHitLabel: "缓存命中率",
    modelLabel: "模型",
    monthlyEstimateLabel: "计费周期内的预估成本",
    per1000Label: "每 1,000 次请求的预估成本",
    totalRequestsLabel: "周期内总请求数",
    scopeTitle: "此估算涵盖的范围",
    scopeBody: "这是基于各厂商标准输入、缓存输入和输出 Token 单价，以美元计的原厂文本生成 API 估算。不包括缓存写入、工具／函数调用 Token、重试、托管费用，以及公开价目表未体现的大额或企业定价。这不是完整的实际账单。",
    noCachePricingNotice: "该模型没有公开的缓存读取价格，因此缓存命中的请求按标准输入价格估算——不假设任何折扣。",
    modelLinkLabel: "查看该模型的完整价格",
  },
  "zh-TW": {
    kicker: "請求量計算器",
    title: "LLM API 成本計算器",
    intro: "根據每日請求量、每次請求的 Token 數以及快取命中率估算每月 API 支出。",
    dailyRequestsLabel: "每日請求數",
    inputTokensLabel: "每次請求輸入 Token",
    outputTokensLabel: "每次請求輸出 Token",
    billingDaysLabel: "計費天數",
    cacheHitLabel: "快取命中率",
    modelLabel: "模型",
    monthlyEstimateLabel: "計費期間的預估成本",
    per1000Label: "每 1,000 次請求的預估成本",
    totalRequestsLabel: "期間內總請求數",
    scopeTitle: "此估算涵蓋的範圍",
    scopeBody: "這是根據各廠商標準輸入、快取輸入與輸出 Token 單價，以美元計算的原廠文字生成 API 估算。不包含快取寫入、工具／函式呼叫 Token、重試、託管費用，以及公開價目表未反映的大量或企業定價。這並非完整的實際帳單。",
    noCachePricingNotice: "此模型沒有公開的快取讀取價格，因此命中快取的請求以標準輸入價格估算——不假設任何折扣。",
    modelLinkLabel: "檢視此模型的完整價格",
  },
  ko: {
    kicker: "요청량 계산기",
    title: "LLM API 비용 계산기",
    intro: "일일 요청 수, 요청당 토큰 수, 캐시 적중률을 기준으로 월간 API 비용을 추정합니다.",
    dailyRequestsLabel: "일일 요청 수",
    inputTokensLabel: "요청당 입력 토큰",
    outputTokensLabel: "요청당 출력 토큰",
    billingDaysLabel: "청구 일수",
    cacheHitLabel: "캐시 적중률",
    modelLabel: "모델",
    monthlyEstimateLabel: "청구 기간 예상 비용",
    per1000Label: "요청 1,000건당 예상 비용",
    totalRequestsLabel: "기간 내 총 요청 수",
    scopeTitle: "이 추정치가 포함하는 범위",
    scopeBody: "각 공급업체의 표준 입력, 캐시 입력, 출력 토큰 요금을 사용한 원본 텍스트 생성 API의 미국 달러 추정치입니다. 캐시 쓰기, 도구/함수 호출 토큰, 재시도, 호스팅, 공개 요금표에 반영되지 않은 대량 또는 엔터프라이즈 요금제는 제외됩니다. 실제 청구서 전체가 아닙니다.",
    noCachePricingNotice: "이 모델에는 공개된 캐시 읽기 요금이 없으므로 캐시된 요청은 표준 입력 요금으로 추정되며, 할인은 가정하지 않습니다.",
    modelLinkLabel: "이 모델의 전체 가격 보기",
  },
};

export function requestCostCopy(language: Language): RequestCostCopy {
  return copy[language];
}
